/** Role helpers for Signal Bridge Training (Phase A). */

export const TRAINING_ROLES = new Set([
  'tenant_admin',
  'operator',
  'support_user',
  'admin',
  'superadmin',
]);

export function canAccessTraining(role) {
  return TRAINING_ROLES.has(String(role || ''));
}

export function trainingRoleLabel(rolePath) {
  switch (rolePath) {
    case 'tenant_admin':
      return 'Tenant Administrator';
    case 'operator':
      return 'Operator';
    case 'support':
      return 'Support';
    default:
      return 'Training';
  }
}

export function statusChipColor(status) {
  switch (status) {
    case 'completed':
      return 'success';
    case 'in_progress':
      return 'primary';
    default:
      return 'default';
  }
}

export function statusLabel(status) {
  switch (status) {
    case 'completed':
      return 'Complete';
    case 'in_progress':
      return 'In Progress';
    default:
      return 'Not Started';
  }
}

export function procedureStatusColor(status) {
  switch (status) {
    case 'SUPPORTED':
      return 'success';
    case 'PARTIALLY_SUPPORTED':
      return 'warning';
    case 'UI_MISSING':
    case 'NOT_SUPPORTED':
      return 'error';
    case 'LIVE_VALIDATION_ONLY':
      return 'info';
    default:
      return 'default';
  }
}

export function procedureStatusLabel(status) {
  return String(status || 'UNKNOWN').replace(/_/g, ' ');
}

/** Learner-facing: true when the taught primary task can be completed. */
export function isPrimaryTaskAvailable(lesson) {
  if (!lesson) return false;
  if (typeof lesson.primaryTaskAvailable === 'boolean') {
    return lesson.primaryTaskAvailable;
  }
  const flags = lesson.statusFlags || [];
  if (flags.includes('SUPPORTED') || lesson.status === 'SUPPORTED') return true;
  if (lesson.status === 'LIVE_VALIDATION_ONLY' || lesson.status === 'PARTIALLY_SUPPORTED') {
    return true;
  }
  return false;
}

/** Internal/AI secondary gaps — do not render as a default learner box. */
export function lessonLimitations(lesson) {
  if (Array.isArray(lesson?.limitations) && lesson.limitations.length) {
    return lesson.limitations;
  }
  return [];
}

/**
 * Learner-facing lesson-level limitations box.
 * Completable lessons never show a QA-style gap dump.
 */
export function shouldShowLessonLimitations(lesson) {
  if (typeof lesson?.showLessonLimitations === 'boolean') {
    return lesson.showLessonLimitations;
  }
  return false;
}

export function shouldShowUnavailableWarning(lesson) {
  if (typeof lesson?.showUnavailableWarning === 'boolean') {
    return lesson.showUnavailableWarning;
  }
  return !isPrimaryTaskAvailable(lesson);
}

export function lessonAvailabilityMessage(lesson) {
  if (lesson?.availabilityMessage) return lesson.availabilityMessage;
  if (shouldShowUnavailableWarning(lesson)) {
    return 'This lesson describes functionality that is not currently available in the Signal Bridge UI. You can still read the workflow, but you cannot complete the core action in the current interface.';
  }
  return '';
}
